/**
 * Utility functions to map between database models and application models
 */

import { Project, ProjectTag, ProjectTagAssignment } from './types/database-types';

/**
 * Convert database project model to application project model
 */
export function mapDbProjectToProject(dbProject: any): Project {
  return {
    id: dbProject.id,
    userId: dbProject.user_id,
    title: dbProject.title,
    overview: dbProject.overview || '',
    isFavorite: dbProject.is_favorite || false,
    isArchived: dbProject.is_archived || false,
    isTrashed: dbProject.is_trashed || false,
    createdAt: dbProject.created_at,
    updatedAt: dbProject.updated_at,
    metadata: dbProject.metadata || {},
    type: dbProject.type,
    displayOrder: dbProject.display_order,
  };
}

/**
 * Convert application project model to database project model
 * Explicitly excludes id, user_id and timestamp fields which should be managed by the database
 */
export function mapProjectToDbProject(project: Partial<Project>): any {
  // Only include fields that should be sent to the database
  // Explicitly exclude id, user_id and timestamp fields
  const dbProject: Record<string, any> = {};
  
  // Map fields with different naming conventions
  if (project.title !== undefined) dbProject.title = project.title;
  if (project.overview !== undefined) dbProject.overview = project.overview;
  if (project.isFavorite !== undefined) dbProject.is_favorite = project.isFavorite;
  if (project.isArchived !== undefined) dbProject.is_archived = project.isArchived;
  if (project.isTrashed !== undefined) dbProject.is_trashed = project.isTrashed;
  if (project.metadata !== undefined) dbProject.metadata = project.metadata;
  
  // Handle new fields
  if (project.type !== undefined) dbProject.type = project.type;
  if (project.displayOrder !== undefined) dbProject.display_order = project.displayOrder;
  
  return dbProject;
}

/**
 * Convert database tag model to application tag model
 */
export function mapDbTagToProjectTag(dbTag: any): ProjectTag {
  return {
    id: dbTag.id,
    name: dbTag.name,
    color: dbTag.color,
    userId: dbTag.user_id,
    createdAt: dbTag.created_at,
  };
}

/**
 * Convert application tag model to database tag model
 */
export function mapProjectTagToDbTag(tag: Omit<ProjectTag, 'id' | 'createdAt'>): any {
  return {
    name: tag.name,
    color: tag.color,
    user_id: tag.userId,
  };
}

/**
 * Convert database tag assignment model to application tag assignment model
 */
export function mapDbTagAssignmentToProjectTagAssignment(dbAssignment: any): ProjectTagAssignment {
  return {
    id: dbAssignment.id,
    tagId: dbAssignment.tag_id,
    pageId: dbAssignment.workbook_id, // Note: keeping pageId name for backward compatibility
    createdAt: dbAssignment.created_at,
  };
}

/**
 * Convert application tag assignment model to database tag assignment model
 */
export function mapProjectTagAssignmentToDbTagAssignment(assignment: Omit<ProjectTagAssignment, 'id' | 'createdAt'>): any {
  return {
    tag_id: assignment.tagId,
    workbook_id: assignment.pageId, // Note: pageId still represents workbook in the interface
  };
}
